import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Typography, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';

import { SumcheckConfigInput, SumcheckPatternConfig } from '../../../utils/fieldValidations';

interface ChargesSumcheckConfigDialogProps {
  open: boolean;
  config: SumcheckConfigInput;
  onClose: () => void;
  onSave: (config: SumcheckConfigInput) => void;
}

const emptyPattern: SumcheckPatternConfig = { pattern: '', attribute: 'tags' };

const ChargesSumcheckConfigDialog: React.FC<ChargesSumcheckConfigDialogProps> = ({
  open,
  config,
  onClose,
  onSave,
}) => {
  const [localConfig, setLocalConfig] = useState<SumcheckConfigInput>({
    include: [],
    exclude: [],
  });

  // Track previous open state to only initialize when transitioning from closed to open
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only reset when transitioning from closed to open
    if (open && !prevOpenRef.current) {
      setLocalConfig({
        include: config.include.length > 0 ? [...config.include] : [{ ...emptyPattern }],
        exclude: config.exclude.length > 0 ? [...config.exclude] : [{ ...emptyPattern }],
      });
    }
    prevOpenRef.current = open;
  }, [open, config]);

  const handlePatternChange = (
    section: 'include' | 'exclude',
    index: number,
    field: keyof SumcheckPatternConfig,
    value: string
  ) => {
    setLocalConfig((prev) => {
      const newSection = [...prev[section]];
      newSection[index] = { ...newSection[index], [field]: value };
      return { ...prev, [section]: newSection };
    });
  };

  const handleAddPattern = (section: 'include' | 'exclude') => {
    setLocalConfig((prev) => ({
      ...prev,
      [section]: [...prev[section], { ...emptyPattern }],
    }));
  };

  const handleRemovePattern = (section: 'include' | 'exclude', index: number) => {
    setLocalConfig((prev) => {
      const newSection = prev[section].filter((_, i) => i !== index);
      return { ...prev, [section]: newSection };
    });
  };

  const handleSubmit = () => {
    // Filter out empty patterns before saving
    const cleanedConfig: SumcheckConfigInput = {
      include: localConfig.include.filter((p) => p.pattern.trim() !== ''),
      exclude: localConfig.exclude.filter((p) => p.pattern.trim() !== ''),
    };
    onSave(cleanedConfig);
    onClose();
  };

  const renderPatternSection = (section: 'include' | 'exclude', title: string) => {
    const patterns = localConfig[section];

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {title}
          </Typography>
          <Tooltip title={`Add ${section} pattern`} placement="top">
            <IconButton
              size="small"
              onClick={() => handleAddPattern(section)}
              data-cy={`sumcheck-config-add-${section}`}
              sx={{ p: 0.25 }}
            >
              <AddIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {patterns.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}
          >
            None.{' '}
            <Button
              size="small"
              onClick={() => handleAddPattern(section)}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontSize: '0.8rem' }}
            >
              Add one
            </Button>
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {patterns.map((pattern, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                }}
              >
                <TextField
                  select
                  value={pattern.attribute}
                  onChange={(e) =>
                    handlePatternChange(
                      section,
                      index,
                      'attribute',
                      e.target.value as 'tags' | 'label'
                    )
                  }
                  size="small"
                  variant="outlined"
                  data-cy={`sumcheck-config-${section}-attribute-${index}`}
                  sx={{ width: 90 }}
                  SelectProps={{ sx: { fontSize: '0.875rem' } }}
                >
                  <MenuItem value="tags">Tags</MenuItem>
                  <MenuItem value="label">Label</MenuItem>
                </TextField>
                <TextField
                  value={pattern.pattern}
                  onChange={(e) => handlePatternChange(section, index, 'pattern', e.target.value)}
                  size="small"
                  variant="outlined"
                  placeholder="Pattern"
                  data-cy={`sumcheck-config-${section}-pattern-${index}`}
                  sx={{ flex: 2 }}
                  InputProps={{ sx: { fontSize: '0.875rem' } }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemovePattern(section, index)}
                  data-cy={`sumcheck-config-remove-${section}-${index}`}
                  sx={{ p: 0.25, color: 'error.main' }}
                >
                  <CloseIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <DialogGlynt open={open} closeModal={onClose} cypressId="dialog-sumcheck-config" width="400px">
      <DialogTitleGlynt title="Sumcheck Config" closeModal={onClose} />
      <DialogContentGlynt scrollable>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            Customize the sumcheck validation rule for this field.
          </Typography>

          {renderPatternSection('include', 'Include')}
          {renderPatternSection('exclude', 'Exclude')}
        </Box>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="sumcheck-config-cancel"
        submitCypressId="sumcheck-config-save"
        clearCypressId=""
        submitButtonText="Save"
        showClearButton={false}
        showCancelButton={true}
        showSubmitButton={true}
      />
    </DialogGlynt>
  );
};

export default ChargesSumcheckConfigDialog;
